// frontend/src/utils/frame/frameRules.js

const FRAME_TYPES = {
  CASUAL: 'casual',
  PLANNING: 'planning',
  DRAFTING: 'drafting',
  EDITING: 'editing',
  EVALUATION: 'evaluation',
  APPROVAL: 'approval',
  DEBUGGING: 'debugging',
  RESET: 'reset',
};

const FRAME_RULES = {
  [FRAME_TYPES.CASUAL]: {
    type: FRAME_TYPES.CASUAL,
    confidence: 0.35,
    allowedBehaviors: ['chat', 'reflect', 'answer', 'ask_followup'],
    blockedBehaviors: [],
    triggers: [
      'hello',
      'hi',
      'hey',
      'how are you',
      'just wondering',
      'just checking',
      'what do you think',
    ],
    hardLockTriggers: [],
  },

  [FRAME_TYPES.PLANNING]: {
    type: FRAME_TYPES.PLANNING,
    confidence: 0.7,
    allowedBehaviors: ['plan', 'brainstorm', 'outline', 'sequence', 'suggest_next_steps'],
    blockedBehaviors: ['finalize'],
    triggers: [
      'plan',
      'planning',
      'roadmap',
      'next steps',
      'what should we do',
      'how should we approach',
      'strategy',
      'organize',
      'figure out',
    ],
    hardLockTriggers: [],
  },

  [FRAME_TYPES.DRAFTING]: {
    type: FRAME_TYPES.DRAFTING,
    confidence: 0.72,
    allowedBehaviors: ['draft', 'write', 'rewrite', 'compose', 'expand'],
    blockedBehaviors: ['finalize_without_confirmation'],
    triggers: [
      'draft',
      'write',
      'rewrite',
      'redraft',
      'compose',
      'put it together',
      'turn this into',
      'make this into',
    ],
    hardLockTriggers: [],
  },

  [FRAME_TYPES.EDITING]: {
    type: FRAME_TYPES.EDITING,
    confidence: 0.82,
    allowedBehaviors: ['edit', 'tighten', 'revise', 'clean_up', 'adjust'],
    blockedBehaviors: ['restructure_without_instruction'],
    triggers: [
      'edit',
      'tighten',
      'revise',
      'clean this up',
      'fix this',
      'adjust',
      'make it better',
      'improve this',
      'correct this',
    ],
    hardLockTriggers: [],
  },

  [FRAME_TYPES.EVALUATION]: {
    type: FRAME_TYPES.EVALUATION,
    confidence: 0.84,
    allowedBehaviors: ['assess', 'evaluate', 'compare', 'judge', 'explain'],
    blockedBehaviors: ['rewrite_without_instruction'],
    triggers: [
      'evaluate',
      'assessment',
      'assess',
      'review',
      'what do you think',
      'is this good',
      'does this work',
      'analyze',
      'explain why',
    ],
    hardLockTriggers: [],
  },

  [FRAME_TYPES.APPROVAL]: {
    type: FRAME_TYPES.APPROVAL,
    confidence: 0.92,
    allowedBehaviors: ['confirm', 'approve', 'hold', 'summarize'],
    blockedBehaviors: ['rewrite', 're_optimize', 'restructure'],
    triggers: [
      'final',
      'finalize',
      'approved',
      'looks good',
      'ship it',
      'done',
      'posted',
      'leave it',
      "don't edit",
      'do not edit',
      'lock it in',
    ],
    hardLockTriggers: [
      'final',
      'posted',
      'leave it',
      "don't edit",
      'do not edit',
      'lock it in',
    ],
  },

  [FRAME_TYPES.DEBUGGING]: {
    type: FRAME_TYPES.DEBUGGING,
    confidence: 0.9,
    allowedBehaviors: ['trace', 'diagnose', 'fix', 'verify', 'narrow_scope'],
    blockedBehaviors: ['rewrite_unrelated_sections'],
    triggers: [
      'debug',
      'bug',
      'error',
      'trace',
      'why is this happening',
      'what broke',
      'fix',
      'failing',
      'compile',
      'syntax error',
      'log',
      'stack trace',
    ],
    hardLockTriggers: [],
  },

  [FRAME_TYPES.RESET]: {
    type: FRAME_TYPES.RESET,
    confidence: 0.25,
    allowedBehaviors: ['chat', 'answer'],
    blockedBehaviors: ['carry_forward_stale_context'],
    triggers: [],
    hardLockTriggers: [],
  },
};

const FRAME_PRIORITY = [
  FRAME_TYPES.DEBUGGING,
  FRAME_TYPES.APPROVAL,
  FRAME_TYPES.EDITING,
  FRAME_TYPES.EVALUATION,
  FRAME_TYPES.DRAFTING,
  FRAME_TYPES.PLANNING,
  FRAME_TYPES.CASUAL,
];

function normalizeText(value = '') {
  return String(value).toLowerCase().trim();
}

function getDefaultFrame() {
  const casual = FRAME_RULES[FRAME_TYPES.CASUAL];

  return {
    type: casual.type,
    confidence: casual.confidence,
    locked: false,
    lastUpdatedBy: 'default',
    allowedBehaviors: [...casual.allowedBehaviors],
    blockedBehaviors: [...casual.blockedBehaviors],
    matchedTriggers: [],
  };
}

function getResetFrame(previousFrame = null) {
  const reset = FRAME_RULES[FRAME_TYPES.RESET];

  return {
    type: reset.type,
    confidence: reset.confidence,
    locked: false,
    lastUpdatedBy: 'fresh_open_reset',
    allowedBehaviors: [...reset.allowedBehaviors],
    blockedBehaviors: [...reset.blockedBehaviors],
    matchedTriggers: [],
    previousFrameType: previousFrame?.type || null,
  };
}

export {
  FRAME_TYPES,
  FRAME_RULES,
  FRAME_PRIORITY,
  normalizeText,
  getDefaultFrame,
  getResetFrame,
};